import { CustomModal, FormInput, FormSelector } from "@/Components";
import { Col, Form, Row } from "antd";
import React, { useState, useMemo } from "react";
import style from "./style.module.scss";
import {
  useConfigureAlmostAvailableAlgo,
  useConfigureFindNearestAlgo,
} from "@/hooks/algorithmconfigHook";
import { useForm } from "antd/es/form/Form";
import { toast } from "react-toastify";
import { useRetrieveTitle } from "@/Utils/helpers";
import { ALGORITHM_TYPE } from "@/Constants";

interface AlgorithmInterface {
  serviceId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AlgorithmModel: React.FC<AlgorithmInterface> = ({
  serviceId,
  open,
  setOpen,
}) => {
  const [algorithmForm] = useForm();
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string | null>(null);

  const { mutateAsync: findNearestAlgo, isPending: isFinNearestPending } =
    useConfigureFindNearestAlgo();
  const { mutateAsync: almostAvailable, isPending: isAlmostPending } =
    useConfigureAlmostAvailableAlgo();

  const { getTitle } = useRetrieveTitle();

  const isLoading = useMemo(
    () => isFinNearestPending || isAlmostPending,
    [isFinNearestPending, isAlmostPending]
  );

  const handleAlgorithmChange = (value: string) => setSelectedAlgorithm(value);

  const algorithmOptions = useMemo(() => {
    return ALGORITHM_TYPE.map((algo) => {
      const translated = getTitle(algo.value);

      return {
        value: algo.value,
        label: translated ? translated : algo.label,
      };
    });
  }, [getTitle]);

  const handleServiceAlgorithmCreate = async (values: any) => {
    try {
      const isFindNearest = values.algorithmType === "find_nearest";

      const config = {
        pickup_radius_km: Number(values.pickup_radius_km),
        bounding_box_radius_km: Number(values.bounding_box_radius_km),
        ...(isFindNearest
          ? {}
          : {
            dest_radius_km: Number(values.dest_radius_km),
            max_jobs: Number(values.max_jobs),
          }),
      };

      const data = {
        service_id: serviceId,
        algo_type: values.algorithmType,
        priority: 1,
        config,
      };

      const result = isFindNearest
        ? await findNearestAlgo(data)
        : await almostAvailable(data);

      if (!result.success) {
        toast.error(result.details?.error || "An error occurred");
        return;
      }

      toast.success(
        isFindNearest
          ? "Find Nearest Algorithm Successfully Configured"
          : "Almost Available Algorithm Successfully Configured"
      );
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      algorithmForm.resetFields();   // ✅ reset after submit
      setSelectedAlgorithm(null);
      setOpen(false);
    }
  };

  const renderAlgorithmFields = () => {
    const commonFields = (
      <>
        <Form.Item
          name="pickup_radius_km"
          rules={[{ required: true, message: getTitle("pickup_radius_required_error") }]}
        >
          <FormInput
            title={getTitle("pickup_radius_km_label")}
            placeholder={getTitle("enter_pickup_radius_km")}
          />
        </Form.Item>
        <Form.Item
          name="bounding_box_radius_km"
          rules={[
            { required: true, message: getTitle("bounding_box_radius_required_error") },
          ]}
        >
          <FormInput
            // defaultValue={15}
            title={getTitle("bounding_box_radius_km_label")}
            placeholder={getTitle("enter_bounding_box_radius_km")}
          />
        </Form.Item>
      </>
    );

    if (selectedAlgorithm === "find_nearest") {
      return commonFields;
    }

    if (selectedAlgorithm === "almost_available") {
      return (
        <>
          {commonFields}
          <Form.Item
            name="dest_radius_km"
            rules={[{ required: true, message: getTitle("dest_radius_required_error") }]}
          >
            <FormInput
              // defaultValue={5}
              title={getTitle("dest_radius_km_label")}
              placeholder={getTitle("enter_dest_radius_km")}
            />
          </Form.Item>
          <Form.Item
            name="max_jobs"
            rules={[{ required: true, message: getTitle("max_jobs_required_error") }]}
          >
            <FormInput
              // defaultValue={1}
              title={getTitle("max_jobs_label")}
              placeholder={getTitle("enter_max_jobs")}
            />
          </Form.Item>
        </>
      );
    }

    return null;
  };

  return (
    <CustomModal
      open={open}
      destroyOnClose
      title={getTitle("create_configuration")}
      handleCancel={() => {
        algorithmForm.resetFields();
        setOpen(false);
        setSelectedAlgorithm(null);
      }}
      handleOk={algorithmForm.submit}
      className={style.createConfigModal}
      okButtonProps={{
        disabled: !selectedAlgorithm,
        style: { width: "100px", height: "40px" },
      }}
      confirmLoading={isLoading}
      okText={getTitle("create")}
      cancelText={getTitle("cancel")}
    >
      <Form form={algorithmForm} onFinish={handleServiceAlgorithmCreate}>
        <Form.Item
          name="algorithmType"
          rules={[{ required: true, message: getTitle("algorithm_empty_error") }]}
          className={style.formitem}
        >
          <FormSelector
            title={getTitle("select_algorithm_type") || "Algorithm Type"}
            placeholder={getTitle("select_algorithm_type")}
            options={algorithmOptions}
            onChange={handleAlgorithmChange}
          />
        </Form.Item>
        <Row gutter={0}>
          <Col span={24} className={style.createAlgoInput}>{renderAlgorithmFields()}</Col>
        </Row>
      </Form>
    </CustomModal>
  );
};

export default AlgorithmModel;
